import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ConversationAnchorType,
  type ConversationAnchorSummary,
  type ConversationAnchorType as AnchorType,
} from '@scoutaround/script-breakdown-shared';

import { api, type ProjectPerson } from '../../lib/api.js';
import { useMembership } from '../../lib/permissions.js';

/**
 * TEAM CONVERSATIONS IN THE BROWSER (ADR 0021).
 *
 * The switch comes from the server (`/me`'s `features.conversations`, the Laravel
 * flag riding on the session). Unlike the permission hints, UNKNOWN reads OFF:
 * offering a feature the platform has switched off is worse than showing it a
 * moment late, and the endpoints refuse anyway.
 */
export function useConversationsEnabled(): boolean {
  return useMembership()?.features?.conversations === true;
}

export function useHostMessagesEnabled(): boolean {
  return useMembership()?.features?.hostMessages === true;
}

export function useMyUserId(): number | null {
  return useMembership()?.userId ?? null;
}

/**
 * Every badge on every surface reads this ONE answer, polled every 30 s while the
 * tab is visible (react-query pauses an interval in a hidden tab by default).
 */
export function useConversationSummary(projectId: string) {
  const enabled = useConversationsEnabled();
  return useQuery({
    queryKey: ['conversation-summary', projectId],
    queryFn: () => api.conversationSummary(projectId),
    enabled: enabled && projectId !== '',
    refetchInterval: 30_000,
    staleTime: 10_000,
    retry: false,
  });
}

export function useAnchorSummary(
  projectId: string,
  anchorType: AnchorType,
  anchorId: string,
): ConversationAnchorSummary | null {
  const summary = useConversationSummary(projectId);
  return useMemo(
    () =>
      summary.data?.anchors.find((a) => a.anchorType === anchorType && a.anchorId === anchorId) ??
      null,
    [summary.data, anchorType, anchorId],
  );
}

/**
 * The production's places and script locations, for the place preview, the
 * location chips in messages and the composer's picker — one read per
 * production, shared by every thread on screen.
 */
export function useConversationLocations(projectId: string) {
  const enabled = useConversationsEnabled();
  return useQuery({
    queryKey: ['conversation-locations', projectId],
    queryFn: () => api.conversationLocations(projectId),
    enabled: enabled && projectId !== '',
    staleTime: 60_000,
    retry: false,
  });
}

/** Names for the production's members. With `anchorType`, who may read a thread on it. */
export function usePeople(projectId: string, anchorType?: AnchorType) {
  return useQuery({
    queryKey: ['project-people', projectId, anchorType ?? 'all'],
    queryFn: () => api.projectPeople(projectId, anchorType),
    enabled: projectId !== '',
    staleTime: 5 * 60_000,
  });
}

export function personName(
  people: readonly ProjectPerson[] | undefined,
  userId: number,
  me: number | null,
): string {
  if (me !== null && userId === me) return 'You';
  return people?.find((p) => p.userId === userId)?.displayName ?? 'Member';
}

export interface DiscussTarget {
  readonly anchorType: AnchorType;
  readonly anchorId: string;
  readonly label?: string;
}

/**
 * THE DRAWER HAS AN ADDRESS (`?discuss=TYPE:id&thread=id`), like every state here,
 * so a conversation can be linked, refreshed and walked back to. Other query
 * parameters on the page are kept.
 */
export function useDiscuss() {
  const [params, setParams] = useSearchParams();

  const raw = params.get('discuss');
  const discuss = useMemo<DiscussTarget | null>(() => {
    if (raw === null) return null;
    const at = raw.indexOf(':');
    if (at === -1) return null;
    const parsed = ConversationAnchorType.safeParse(raw.slice(0, at));
    const anchorId = raw.slice(at + 1);
    return parsed.success && anchorId !== '' ? { anchorType: parsed.data, anchorId } : null;
  }, [raw]);
  const threadId = params.get('thread');

  const update = useCallback(
    (change: (next: URLSearchParams) => void) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          change(next);
          return next;
        },
        { replace: false },
      );
    },
    [setParams],
  );

  return {
    discuss,
    threadId,
    open: (target: DiscussTarget, thread?: string) => {
      update((next) => {
        next.set('discuss', `${target.anchorType}:${target.anchorId}`);
        if (thread) next.set('thread', thread);
        else next.delete('thread');
      });
    },
    openThread: (thread: string | null) => {
      update((next) => {
        if (thread) next.set('thread', thread);
        else next.delete('thread');
      });
    },
    close: () => {
      update((next) => {
        next.delete('discuss');
        next.delete('thread');
      });
    },
  };
}

/** "Sc. 14 — …", "Train station, night" … as the anchor's own words. */
export const ANCHOR_NOUN: Readonly<Record<AnchorType, string>> = Object.freeze({
  PROJECT: 'production',
  SCENE: 'scene',
  REQUIREMENT: 'script location',
  PLACE: 'place',
  ELEMENT: 'breakdown element',
  REFERENCE: 'reference',
});

/** A request key for idempotent creates and posts. */
export function newRequestKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // A v4-shaped fallback for an old test runtime; never reached in a supported browser.
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-8${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}
